import { collection, addDoc, doc, getDocs, query, where, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { BillData, Company, PaymentRecord, DeliveryChallan } from '../types/billing';
import { QuotationRequest } from '../types';

// Collections
const companiesCol = collection(db, 'companies');
const billsCol = collection(db, 'bills');
const paymentsCol = collection(db, 'payments');
const quotationsCol = collection(db, 'quotations');
const deliveryChallansCol = collection(db, 'deliveryChallans');

// Quotations
export async function createQuotation(quotation: QuotationRequest): Promise<string> {
  const docRef = await addDoc(quotationsCol, quotation);
  return docRef.id;
}

export async function listAllQuotations(): Promise<QuotationRequest[]> {
  const snap = await getDocs(quotationsCol);
  return snap.docs.map(d => ({ ...(d.data() as QuotationRequest), id: d.id }));
}

// Update an existing quotation by ID
export async function updateQuotation(
  quotationId: string,
  update: Partial<QuotationRequest>
): Promise<void> {
  await updateDoc(doc(db, 'quotations', quotationId), update);
}

export async function createCompany(company: Company) {
  await addDoc(companiesCol, company);
}

export async function listCompanies(): Promise<Company[]> {
  const snap = await getDocs(companiesCol);
  return snap.docs.map(d => ({ ...(d.data() as Company), id: d.id }));
}

export async function updateCompany(company: Company) {
  if (!company.id) throw new Error('Company ID required for update');
  const { id, ...companyData } = company;
  await updateDoc(doc(db, 'companies', company.id), companyData);
}

export async function deleteCompany(companyId: string) {
  await deleteDoc(doc(db, 'companies', companyId));
}

export async function generateNextInvoiceNumber(clientId: string): Promise<string> {
  try {
    console.log(`Generating invoice number for client: ${clientId}`);

    // Fixed prefix format: AE/GST/25-26/
    const prefix = 'AE/GST/26-27';
    console.log(`Using invoice prefix: ${prefix}`);

    // Get all bills (not just for this company, to ensure unique invoice numbers across all clients)
    const allBills = await listAllBills();
    console.log(`Found ${allBills.length} total bills`);

    // Filter bills that match the prefix format "AE/GST/25-26/XXX"
    const matchingBills = allBills.filter(bill => {
      if (!bill.invoiceNumber || bill.invoiceNumber.trim() === '') return false;
      // Check if invoice number starts with the prefix
      return bill.invoiceNumber.startsWith(prefix);
    });
    
    console.log(`Found ${matchingBills.length} bills with prefix ${prefix}`);

    if (matchingBills.length === 0) {
      // If no bills exist with this prefix, start with 001
      const invoiceNumber = `${prefix}/001`;
      console.log(`No existing bills found with prefix ${prefix}, starting with: ${invoiceNumber}`);
      return invoiceNumber;
    }

    // Extract the last 3-digit number from invoice numbers like "AE/GST/25-26/001"
    const invoiceNumbers = matchingBills
      .map(bill => {
        // Extract the number after the last slash (e.g., "001" from "AE/GST/25-26/001")
        const parts = bill.invoiceNumber.split('/');
        const lastPart = parts[parts.length - 1];
        const num = parseInt(lastPart, 10);
        console.log(`Bill ${bill.id}: invoice number "${bill.invoiceNumber}" -> extracted number: ${num}`);
        return isNaN(num) ? 0 : num;
      })
      .filter(num => num > 0);

    const highestNumber = Math.max(...invoiceNumbers, 0);
    const nextNumber = highestNumber + 1;

    // Format with zero padding (001, 002, 003, etc.)
    const paddedNumber = nextNumber.toString().padStart(3, '0');
    const invoiceNumber = `${prefix}/${paddedNumber}`;

    console.log(`Highest invoice number found: ${highestNumber}, next number: ${nextNumber}, formatted: ${invoiceNumber}`);

    return invoiceNumber;
  } catch (error) {
    console.error('Error generating invoice number:', error);
    // Fallback: use the fixed prefix format
    const fallbackNumber = 'AE/GST/25-26/001';
    console.log(`Using fallback invoice number: ${fallbackNumber}`);
    return fallbackNumber;
  }
}

export async function createBill(bill: BillData): Promise<string> {
  const docRef = await addDoc(billsCol, bill);
  return docRef.id;
}

export async function listBillsByCompany(companyId: string): Promise<BillData[]> {
  const q = query(billsCol, where('clientId', '==', companyId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...(d.data() as BillData), id: d.id }));
}

export async function listAllBills(): Promise<BillData[]> {
  const snap = await getDocs(billsCol);
  return snap.docs.map(d => ({ ...(d.data() as BillData), id: d.id }));
}

export async function updateBill(bill: BillData) {
  if (!bill.id) throw new Error('Bill ID required for update');
  const { id, ...billData } = bill;
  await updateDoc(doc(db, 'bills', bill.id), billData);
}

export async function deleteBill(billId: string) {
  await deleteDoc(doc(db, 'bills', billId));
}

export async function recordPayment(paymentData: Omit<PaymentRecord, 'id'>): Promise<string> {
  const docRef = await addDoc(paymentsCol, paymentData);
  return docRef.id;
}

export async function listPaymentsByCompany(companyId: string): Promise<PaymentRecord[]> {
  if (companyId === 'all') {
    // Get all payments
    const snap = await getDocs(paymentsCol);
    return snap.docs.map(d => ({ ...(d.data() as PaymentRecord), id: d.id }));
  }
  const q = query(paymentsCol, where('companyId', '==', companyId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...(d.data() as PaymentRecord), id: d.id }));
}

export async function listAllPayments(): Promise<PaymentRecord[]> {
  const snap = await getDocs(paymentsCol);
  return snap.docs.map(d => ({ ...(d.data() as PaymentRecord), id: d.id }));
}

export async function updatePayment(payment: PaymentRecord) {
  if (!payment.id) throw new Error('Payment ID required for update');
  const { id, ...paymentData } = payment;
  await updateDoc(doc(db, 'payments', payment.id), paymentData);
}

export async function deletePayment(paymentId: string) {
  await deleteDoc(doc(db, 'payments', paymentId));
}

export async function setCompanyDefault(companyId: string) {
  // naive example: mark all false, one true; in production use a Cloud Function/transaction
  const snap = await getDocs(companiesCol);
  await Promise.all(snap.docs.map(async d => updateDoc(doc(db, 'companies', d.id), { isDefault: d.id === companyId })));
}

// Delivery Challan Functions
export async function generateNextChallanNumber(): Promise<string> {
  try {
    console.log('Generating next challan number');

    // Get all delivery challans
    const allChallans = await listAllDeliveryChallans();
    console.log(`Found ${allChallans.length} delivery challans`);

    if (allChallans.length === 0) {
      // If no challans exist, start with 1
      console.log('No existing challans found, starting with challan number: 1');
      return '1';
    }

    // Extract numbers from existing challan numbers and find the highest
    const challanNumbers = allChallans
      .map(challan => {
        // Extract number from formats like "DC-1", "1", "DC-2", etc.
        const match = challan.challanNumber.match(/\d+/);
        const num = match ? parseInt(match[0], 10) : 0;
        console.log(`Challan ${challan.id}: challan number "${challan.challanNumber}" -> extracted number: ${num}`);
        return num;
      })
      .filter(num => !isNaN(num));

    const highestNumber = Math.max(...challanNumbers, 0);
    const nextNumber = highestNumber + 1;

    console.log(`Highest challan number found: ${highestNumber}, next number: ${nextNumber}`);

    // Return next number with zero padding (001, 002, etc.)
    return nextNumber.toString().padStart(3, '0');
  } catch (error) {
    console.error('Error generating challan number:', error);
    // Fallback to current timestamp if there's an error
    const fallbackNumber = (Date.now() % 1000 + 1).toString().padStart(3, '0');
    console.log(`Using fallback challan number: ${fallbackNumber}`);
    return fallbackNumber;
  }
}

export async function createDeliveryChallan(challan: Omit<DeliveryChallan, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  const now = new Date().toISOString();
  const challanData = {
    ...challan,
    createdAt: now,
    updatedAt: now,
  };
  const docRef = await addDoc(deliveryChallansCol, challanData);
  return docRef.id;
}

export async function listAllDeliveryChallans(): Promise<DeliveryChallan[]> {
  const snap = await getDocs(deliveryChallansCol);
  return snap.docs.map(d => ({ ...(d.data() as DeliveryChallan), id: d.id }));
}

export async function listDeliveryChallansByStatus(status: DeliveryChallan['status']): Promise<DeliveryChallan[]> {
  const q = query(deliveryChallansCol, where('status', '==', status));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...(d.data() as DeliveryChallan), id: d.id }));
}

export async function getDeliveryChallanById(challanId: string): Promise<DeliveryChallan | null> {
  try {
    const docSnap = await getDocs(query(deliveryChallansCol, where('__name__', '==', challanId)));
    if (!docSnap.empty) {
      const doc = docSnap.docs[0];
      return { ...(doc.data() as DeliveryChallan), id: doc.id };
    }
    return null;
  } catch (error) {
    console.error('Error fetching delivery challan:', error);
    return null;
  }
}

export async function updateDeliveryChallan(challan: DeliveryChallan): Promise<void> {
  if (!challan.id) throw new Error('Challan ID required for update');
  const { id, ...challanData } = challan;
  await updateDoc(doc(db, 'deliveryChallans', challan.id), {
    ...challanData,
    updatedAt: new Date().toISOString(),
  });
}

export async function deleteDeliveryChallan(challanId: string): Promise<void> {
  await deleteDoc(doc(db, 'deliveryChallans', challanId));
}

export async function markChallanAsReturned(challanId: string, returnCondition?: string, notes?: string): Promise<void> {
  await updateDoc(doc(db, 'deliveryChallans', challanId), {
    status: 'Returned',
    actualReturnDate: new Date().toISOString(),
    returnCondition: returnCondition || '',
    notes: notes || '',
    updatedAt: new Date().toISOString(),
  });
}

export async function markChallanAsOverdue(challanId: string): Promise<void> {
  await updateDoc(doc(db, 'deliveryChallans', challanId), {
    status: 'Overdue',
    updatedAt: new Date().toISOString(),
  });
}

export async function markChallanAsLost(challanId: string, notes?: string): Promise<void> {
  await updateDoc(doc(db, 'deliveryChallans', challanId), {
    status: 'Lost',
    notes: notes || '',
    updatedAt: new Date().toISOString(),
  });
}

// Get overdue challans (challans that are still active but past their expected return date)
export async function getOverdueChallans(): Promise<DeliveryChallan[]> {
  const activeChallans = await listDeliveryChallansByStatus('Active');
  const now = new Date();

  return activeChallans.filter(challan => {
    if (!challan.expectedReturnDate) return false;
    const expectedReturnDate = new Date(challan.expectedReturnDate);
    return expectedReturnDate < now;
  });
}

// Get challans by employee
export async function getChallansByEmployee(employeeName: string): Promise<DeliveryChallan[]> {
  const q = query(deliveryChallansCol, where('employeeName', '==', employeeName));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...(d.data() as DeliveryChallan), id: d.id }));
}

// Get challans by date range
export async function getChallansByDateRange(startDate: string, endDate: string): Promise<DeliveryChallan[]> {
  const allChallans = await listAllDeliveryChallans();

  return allChallans.filter(challan => {
    const challanDate = new Date(challan.challanDate);
    const start = new Date(startDate);
    const end = new Date(endDate);
    return challanDate >= start && challanDate <= end;
  });
}
